import type { ComponentType } from 'react';
import {
  FiCpu,
  FiShield
} from 'react-icons/fi';

import {
  SKILL_LABELS,
  formatLabel,
  formatModelLabel
} from '../workflowShared';
import type { RunToolCard, RunToolCardKind } from './helpers/runScreenTypes';

const IconCpu = FiCpu as unknown as ComponentType<{ 'aria-hidden'?: boolean }>;
const IconShield = FiShield as unknown as ComponentType<{ 'aria-hidden'?: boolean }>;

const RUN_TOOL_CARDS: RunToolCard[] = [
  {
    id: 'gpt-5-6',
    kind: 'model',
    coverage: 'free',
    title: 'GPT 5.6',
    description: 'Base reasoning model for the free baseline scan.'
  },
  {
    id: 'trail-of-bits',
    kind: 'skill',
    coverage: 'free',
    title: formatLabel('trail-of-bits', SKILL_LABELS),
    description: 'Security tool coverage for state-changing flows, scanner guidance, and remediation quality.',
    logo: '/trail_of_bits_logo.jpg',
    tag: 'Audit methodology'
  },
  {
    id: 'daybreak-blue',
    kind: 'model',
    coverage: 'deep',
    title: formatModelLabel('gpt-daybreak-blue-latest'),
    description: 'Advanced reasoning model for deeper scan passes and finding synthesis.'
  },
  {
    id: 'claude-opus-5',
    kind: 'model',
    coverage: 'deep',
    title: formatModelLabel('claude-opus-5'),
    description: 'Independent model pass for broader reasoning coverage.'
  },
  {
    id: 'pashov',
    kind: 'skill',
    coverage: 'deep',
    title: formatLabel('pashov', SKILL_LABELS),
    description: 'Fast smart-contract review workflow focused on realistic exploit paths and high-signal findings.',
    logo: '/pashovlogo.png',
    tag: 'Audit workflow'
  },
  {
    id: 'sc-auditor',
    kind: 'skill',
    coverage: 'deep',
    title: formatLabel('sc-auditor', SKILL_LABELS),
    description: 'Map-Hunt-Attack style smart-contract review for entry points, invariants, and attack surfaces.',
    tag: 'Attack review'
  },
  {
    id: 'quillshield',
    kind: 'skill',
    coverage: 'deep',
    title: formatLabel('quillshield', SKILL_LABELS),
    description: 'Semantic state review across invariants, reentrancy, oracle manipulation, upgrades, and griefing.',
    logo: '/quillshield.avif',
    tag: 'State analysis'
  },
  {
    id: 'cyfrin',
    kind: 'skill',
    coverage: 'deep',
    title: formatLabel('cyfrin', SKILL_LABELS),
    description: 'Production-grade Solidity standards, Foundry workflow review, testing quality, and secure patterns.',
    logo: '/cyfrin.jpg',
    tag: 'Solidity review'
  },
  {
    id: 'forefy',
    kind: 'skill',
    coverage: 'deep',
    title: formatLabel('forefy', SKILL_LABELS),
    description: 'Protocol-type detection, threat modeling, severity calibration, and report-oriented audit structure.',
    tag: 'Threat model'
  },
  {
    id: 'trail-of-bits-supply-chain',
    kind: 'skill',
    coverage: 'deep',
    title: 'Trail of Bits Supply Chain',
    description: 'Dependency takeover and package risk assessment for web application and API source workspaces.',
    logo: '/trail_of_bits_logo.jpg',
    tag: 'Supply chain'
  },
  {
    id: 'trail-of-bits-static-analysis',
    kind: 'skill',
    coverage: 'deep',
    title: 'Trail of Bits Static Analysis',
    description: 'CodeQL, Semgrep, and SARIF-guided review for source-assisted web and API security analysis.',
    logo: '/trail_of_bits_logo.jpg',
    tag: 'Static analysis'
  },
  {
    id: 'semgrep-code-security',
    kind: 'skill',
    coverage: 'deep',
    title: formatLabel('semgrep-code-security', SKILL_LABELS),
    description: 'Pattern and taint-style review for injection, SSRF, path traversal, secrets, and unsafe configuration.',
    tag: 'Code security'
  }
];

function RunToolLogo({ tool }: { tool: RunToolCard }) {
  if (tool.logo) {
    return <img src={tool.logo} alt="" loading="lazy" />;
  }

  if (tool.kind === 'model') {
    return <IconCpu aria-hidden={true} />;
  }

  return <IconShield aria-hidden={true} />;
}

function RunToolCardView({ tool }: { tool: RunToolCard }) {
  return (
    <article className={`run-tool-card ${tool.kind}`}>
      <div className="run-tool-card-logo" aria-hidden={true}>
        <RunToolLogo tool={tool} />
      </div>
      <div className="run-tool-card-body">
        <h3>{tool.title}</h3>
        <p>{tool.description}</p>
        <div className="run-tool-card-badges" aria-label={`${tool.title} classification`}>
          <span className={`run-tool-kind ${tool.kind}`}>
            {tool.kind === 'model' ? 'AI model' : tool.tag ?? 'Security tool'}
          </span>
        </div>
      </div>
    </article>
  );
}

function RunToolsGroup({
  title,
  kind,
  tools
}: {
  title: string;
  kind: RunToolCardKind;
  tools: RunToolCard[];
}) {
  if (tools.length === 0) {
    return null;
  }

  return (
    <div className={`run-tools-group ${kind}`}>
      <div className="run-tools-group-heading">
        <span className={`run-tools-group-icon ${kind}`} aria-hidden={true}>
          {kind === 'model' ? <IconCpu aria-hidden={true} /> : <IconShield aria-hidden={true} />}
        </span>
        <h3>{title}</h3>
      </div>

      <div className="run-tools-grid">
        {tools.map((tool) => (
          <RunToolCardView key={tool.id} tool={tool} />
        ))}
      </div>
    </div>
  );
}

function RunToolsSection({
  id,
  title,
  summary,
  tools
}: {
  id: string;
  title: string;
  summary: string;
  tools: RunToolCard[];
}) {
  const modelTools = tools.filter((tool) => tool.kind === 'model');
  const skillTools = tools.filter((tool) => tool.kind === 'skill');
  const isCompactPair = modelTools.length === 1 && skillTools.length === 1;

  return (
    <section className="run-tools-section" aria-labelledby={id}>
      <div className="run-tools-section-heading">
        <div>
          <h2 id={id}>{title}</h2>
          <p>{summary}</p>
        </div>
      </div>

      {isCompactPair ? (
        <div className="run-tools-grid run-tools-grid-pair">
          {tools.map((tool) => (
            <RunToolCardView key={tool.id} tool={tool} />
          ))}
        </div>
      ) : (
        <div className="run-tools-groups">
          <RunToolsGroup title="AI models" kind="model" tools={modelTools} />
          <RunToolsGroup title="Security tools" kind="skill" tools={skillTools} />
        </div>
      )}
    </section>
  );
}

export function RunToolsScreen() {
  const deepTools = RUN_TOOL_CARDS.filter((tool) => tool.coverage === 'deep');

  return (
    <main className="run-page-panel run-tools-panel">
      <div className="run-panel-heading run-tools-heading">
        <div>
          <h1>Tools</h1>
          <p>Compare the models and security tools Havu orchestrates for each scan.</p>
        </div>
      </div>

      <RunToolsSection
        id="deep-scan-tools"
        title="Full pentest"
        summary="Havu coordinates multiple frontier models and security tools, then consolidates their independent findings into one pentest report."
        tools={deepTools}
      />
    </main>
  );
}

export { RUN_TOOL_CARDS };
